import { useState, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'

const isStaging = import.meta.env.APP_ENV !== 'production'

// Global log store
const logs = []

export const debugLog = (label, data) => {
    if (!isStaging) return
    const entry = {
        time: new Date().toLocaleTimeString(),
        label,
        data: typeof data === 'string' ? data : JSON.stringify(data, null, 2),
    }
    logs.push(entry)
    console.log(`[DEBUG] ${label}:`, data)
}

export const DebugPopup = () => {
    const [open, setOpen] = useState(false)
    const [, forceUpdate] = useState(0)
    const tapCountRef = useRef(0)
    const tapTimerRef = useRef(null)


    // Triple-tap to open
    const handleTriggerTap = useCallback(() => {
        tapCountRef.current += 1
        if (tapTimerRef.current) clearTimeout(tapTimerRef.current)
        tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0 }, 500)

        if (tapCountRef.current >= 3) {
            tapCountRef.current = 0
            setOpen(true)
            forceUpdate((n) => n + 1)
        }
    }, [])

    const handleCopy = useCallback(() => {
        const text = logs.map((l) => `[${l.time}] ${l.label}: ${l.data}`).join('\n\n')
        navigator.clipboard.writeText(text)
    }, [])
    if (!isStaging) return null

    return (
        <>
            {/* Invisible trigger area - triple tap top-right corner */}
            <Box
                onClick={handleTriggerTap}
                sx={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: 60,
                    height: 60,
                    zIndex: 9998,
                }}
            />

            {open && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 9999,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: '#fff',
                            borderRadius: 2,
                            width: '100%',
                            maxWidth: 400,
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderBottom: '1px solid #eee' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                Debug Logs ({logs.length})
                            </Typography>
                            <IconButton size='small' onClick={() => setOpen(false)}>
                                ✕
                            </IconButton>
                        </Box>

                        {/* Logs */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                            {logs.length === 0 ? (
                                <Typography sx={{ color: '#999', fontSize: 13, textAlign: 'center' }}>
                                    No logs yet
                                </Typography>
                            ) : (
                                logs.map((log, i) => (
                                    <Box key={i} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                                        <Typography sx={{ fontSize: 11, color: '#999' }}>
                                            {log.time}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#501BC0' }}>
                                            {log.label}
                                        </Typography>
                                        <Typography
                                            component='pre'
                                            sx={{
                                                fontSize: 11,
                                                fontFamily: 'monospace',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all',
                                                color: '#333',
                                                bgcolor: '#f5f5f5',
                                                borderRadius: 1,
                                                p: 1,
                                                m: 0,
                                                mt: 0.5,
                                            }}
                                        >
                                            {log.data}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderTop: '1px solid #eee' }}>
                            <Button size='small' variant='contained' onClick={handleCopy} fullWidth>
                                Copy All
                            </Button>
                            <Button size='small' variant='outlined' onClick={() => { logs.length = 0; forceUpdate((n) => n + 1) }} fullWidth>
                                Clear
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    )
}
